/*
 * Copyright (c) by INFODATA-GROUP. All rights reserved.
 * No part of this publication may be reproduced, distributed,
 * or transmitted in any form or by any means, including photocopying, recording,
 * or other electronic or mechanical methods, without the prior written permission of the publisher,
 * except in the case of brief quotations embodied in critical
 * reviews and certain other noncommercial uses permitted by copyright law.
 *  author : vhe
 *  date : 02/03/2021 07:39
 */

import { ModelProperty } from '../engine/model-property';
import { NameValue } from './name-value';
import { Operator } from '../enum/operator.enum';
import { TableFilterType } from '../enum/table-filter-type.enum';
import { Format2Pipe } from '../pipe/format2.pipe';
import { CommonUtils } from './common-utils';

export class Filter {
  public property: ModelProperty;
  public filters: NameValue[];

  constructor(property: ModelProperty, filters: NameValue[]) {
    this.property = property;
    this.filters = filters;
  }

  /**
   * Check if filters can be applied to the data.
   * @param data
   */
  public check(data: any): boolean {
    let result: boolean;
    for (const filter of this.filters) {
      if (
        filter.value === null ||
        filter.value === undefined ||
        filter.value === ''
      ) {
        result = true;
      } else {
        result = this.applyFilter(filter, data);
      }
      if (!result) {
        return false;
      }
    }
    return true;
  }

  private applyFilter(filter: NameValue, data: any): boolean {
    if (filter.name === Operator.contain) {
      const pipe = new Format2Pipe();
      let text: string = pipe.transform(
        data[this.property.id],
        this.property,
        data,
      );
      text = CommonUtils.normalize(text, true);
      const dataSearchMatch = text.match(/data-search="([^"]*)"/);
      if (dataSearchMatch) {
        text = dataSearchMatch[1];
      }
      let filterText = CommonUtils.normalize(filter.value, true);
      if (
        filterText.charAt(0) === '"' &&
        filterText.charAt(filterText.length - 1) === '"'
      ) {
        filterText = filterText.slice(1, filterText.length - 1);
        if (filterText === '') {
          return text === '' || text === '-';
        }
        return filterText.localeCompare(text) === 0;
      } else {
        const start = filterText.charAt(0) === '*';
        const end = filterText.charAt(filterText.length - 1) === '*';
        if (start && end) {
          return text.indexOf(filterText) >= 0;
        }
        if (start) {
          filterText = filterText.slice(1);
          const index = text.indexOf(filterText);
          return index >= 0 && index === text.length - filterText.length;
        } else if (end) {
          filterText = filterText.slice(0, filterText.length - 1);
          return text.indexOf(filterText) === 0;
        }
        return text.indexOf(filterText) >= 0;
      }
    } else {
      return this.checkByOperator(filter, data);
    }
  }

  private checkByOperator(filter: NameValue, data: any): boolean {
    if (this.property.tableFilterType === TableFilterType.boolean) {
      return (
        (filter.value.name === 'Y' && data[this.property.id] === true) ||
        (filter.value.name === 'N' && data[this.property.id] === false)
      );
    }
    const compare: number = this.compare(data[this.property.id], filter.value);
    if (compare === null) {
      return true;
    }
    switch (filter.name) {
      case Operator.equal:
        return compare === 0;
      case Operator.greater:
        return compare > 0;
      case Operator.greaterOrEqual:
        return compare >= 0;
      case Operator.less:
        return compare < 0;
      case Operator.lessOrEqual:
        return compare <= 0;
      case Operator.between:
        return compare === 0;
    }
    return undefined;
  }

  private compare(value: any, filter: any): number {
    switch (this.property.tableFilterType) {
      case TableFilterType.list:
        return this.checkOnList(value, filter);
      case TableFilterType.dateInterval:
        return this.checkDateInterval(value, filter);
      case TableFilterType.timeInterval:
        return this.checkTimeInterval(value, filter);
      case TableFilterType.text:
        return this.compareText(value, filter);
      case TableFilterType.numberInterval:
        return this.checkNumberInterval(value, filter);
    }
    return undefined;
  }

  private compareText(value: any, filter: any): number {
    const val = value.toString().toUpperCase();
    const fil = filter.toString().toUpperCase();
    return val.localeCompare(fil);
  }

  private checkOnList(value: any, filter: any): number {
    if (filter && filter instanceof Array) {
      const id = this.property.getPrimaryKey();
      if (id) {
        return filter.find(
          (f) => f.name === value[id] || (f.name === '#NODATA' && !value[id]),
        )
          ? 0
          : 1;
      } else {
        return filter.find(
          (f) => f.name === value || (f.name === '#NODATA' && !value),
        )
          ? 0
          : 1;
      }
    } else if (filter === '#NODATA' && !value) {
      return 0;
    } else {
      if (!value) return 1; // Handle null value for text search
      return value.toString().toLowerCase().indexOf(filter.toLowerCase()) >= 0
        ? 0
        : 1;
    }
  }

  protected checkDateInterval(value: any, filter: any): number {
    const d = CommonUtils.convertStrToDate(value);
    d.setHours(0, 0, 0, 0);
    const compare = { start: true, end: true };
    if (filter.start) {
      filter.start.setHours(0, 0, 0, 0);
      compare.start = d.getTime() - filter.start.getTime() >= 0;
    }
    if (filter.end) {
      filter.end.setHours(0, 0, 0, 0);
      compare.end = filter.end.getTime() - d.getTime() >= 0;
    }
    if (!compare.end) {
      return 1;
    } else if (!compare.start) {
      return -1;
    } else {
      return 0;
    }
  }

  private checkTimeInterval(value: any, filter: any): number {
    const compare = { start: true, end: true };
    const vMin = CommonUtils.convertStrToTimeInMillis(value);
    if (filter.start) {
      const startMin = CommonUtils.convertStrToTimeInMillis(filter.start);
      compare.start = vMin - startMin >= 0;
    }
    if (filter.end) {
      const endMin = CommonUtils.convertStrToTimeInMillis(filter.end);
      compare.end = endMin - vMin >= 0;
    }
    if (!compare.end) {
      return 1;
    } else if (!compare.start) {
      return -1;
    } else {
      return 0;
    }
  }

  private checkNumberInterval(value: any, filter: any): number {
    const compare = { start: true, end: true };
    const val = Number(value);
    if (filter.start) {
      const min = Number(filter.start);
      compare.start = min <= val;
    }

    if (filter.end) {
      const max = Number(filter.end);
      compare.end = max >= val;
    }

    if (!compare.end) {
      return 1;
    }
    if (!compare.start) {
      return -1;
    }
    return 0;
  }
}
